/* global BigInt */

import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import { BrowserProvider, Contract, JsonRpcProvider, parseUnits, formatUnits, utils } from "ethers";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ERC20_ABI } from './erc20';
import { HYPERLANE_BRIDGE_ABI, UNISWAP_V2_ROUTER_ABI, UNISWAP_V3_ROUTER_ABI } from "./crosschain-bridge";
import "./SwapCard.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown } from '@fortawesome/free-solid-svg-icons';

const NETWORK_NAME_MAP = {
  'ETH': { chainName: 'ETH', moralisChain: 'eth' },
  'BSC': { chainName: 'BSC', moralisChain: 'bsc' },
  'BASE': { chainName: 'Base', moralisChain: 'base' }
};

const chains = [
  {
    id: 1,
    name: 'ETH',
    symbol: 'eth',
    rpcUrl: 'https://site1.moralis-nodes.com/eth/e59189c97b604826b7618e7a808c01b3',
    usdc: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
    native: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2',
    nativesymbol: "ETH",
    uniswapV2RouterAddress: '0x7a250d5630B4cF539739dF2C5dAcb4c659F2488D',
    image: 'https://raw.githubusercontent.com/divechan/brandkit/main/eth.png'
  },
  {
    id: 8453,
    name: 'Base',
    symbol: 'base',
    rpcUrl: 'https://base-rpc.publicnode.com',
    usdc: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913', // USDC address
    native: '0x4200000000000000000000000000000000000006', // Native (WETH-equivalent) address
    nativesymbol: "ETH",
    uniswapV2RouterAddress: '0x4752ba5DBc23f44D87826276BF6Fd6b1C372aD24',
    image: 'https://raw.githubusercontent.com/divechan/brandkit/main/base.png'
  },
  {
    id: 56,
    name: 'BSC',
    symbol: 'bsc',
    rpcUrl: 'https://bsc-pokt.nodies.app',
    usdc: '0xe9e7CEA3DedcA5984780Bafc599bD69ADd087D56',
    native: '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c',
    nativesymbol: "BNB",
    uniswapV2RouterAddress: '0x10ED43C718714eb63d5aA57B78B54704E256024E',
    image: 'https://raw.githubusercontent.com/divechan/brandkit/main/bsc.png'
  },
  {
    id: 6278,
    name: 'Rails',
    symbol: 'rails',
    rpcUrl: 'https://mainnet.steamexchange.io',
    usdc: '0x0000000000000000000000000000000000627801',
    native: '0x0000000000000000000000000000000000627800',
    nativesymbol: "STEAMX",
    uniswapV2RouterAddress: '0xba01646Cf1165Db724b2400DBBd713A6a23B378C',
    image: '' // Fallback or no image
  }
];

const outTokens = [ 
  {
    chainId: 1,
    token_address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
    decimals: 6,
    symbol: 'USDC',
    isNative: false,
    logo: '',
  },
  {
    chainId: 8453,
    token_address: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
    decimals: 6,
    symbol: 'USDC',
    isNative: false,
    logo: '',
  },
  {
    chainId: 56,
    token_address: '0xe9e7CEA3DedcA5984780Bafc599bD69ADd087D56',
    decimals: 18,
    symbol: 'BUSD',
    isNative: false,
    logo: '',
  },
  {
    chainId: 6278,
    token_address: '0x0000000000000000000000000000000000627801',
    decimals: 6,
    symbol: 'USDC',
    isNative: false,
    logo: '',
  }
];

// Define WSTEAMX Token Details
const WSTEAMX = {
  address: '0x0000000000000000000000000000000000627800',
  decimals: 18,
};

// Define Contracts
const contracts = {
  eth: '0x7C896F2136A3b1C136fd99720A8a2A3eaE1721E9',
  base: '0x3b41379e766Be73e1048F44dCD1c57Fb88592008',
  bsc: '0x551cD27287C71C07c42573e4AB94FF411b268392',
  rails: '0x3e7812067188B939dca275311ef48F6422a00Ad7',
};

// TokenSelectorButton Component
const TokenSelectorButton = ({ logo, tokenName, network, onClick }) => {
  const networkGradient = {
    ETH: "from-blue-600 via-blue-500 to-blue-400",
    BSC: "from-yellow-600 via-yellow-500 to-yellow-400",
    BASE: "from-blue-900 via-blue-800 to-blue-700",
  };

  return (
    <div
      className={`flex items-center relative bg-gradient-to-r ${
        networkGradient[network] || "from-gray-800 via-gray-800 to-gray-900"
      } p-2 rounded-xl shadow-md cursor-pointer`}
      onClick={onClick}
    >
      {logo && (
        <div className="flex items-center justify-center bg-gray-900 rounded-full shadow-inner w-14 h-14">
          <img src={logo} alt="Logo" className="w-12 h-12 rounded-full" />
        </div>
      )}
      <button className="flex items-center ml-3 text-white focus:outline-none">
        <span className="text-lg font-medium">
          {tokenName || (network ? "Select a token" : "Select a network")}
        </span>
        <svg
          className="w-4 h-4 ml-2 text-gray-400"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  );
};

// TokenModal Component
const TokenModal = ({
  isOpen,
  onClose,
  onSelectNetwork,
  onSelectToken,
  selectedNetwork,
  account,
}) => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchAddress, setSearchAddress] = useState("");
  const [manualToken, setManualToken] = useState(null);
  const [filterEnabled, setFilterEnabled] = useState(
    JSON.parse(localStorage.getItem("filterEnabled")) ?? true
  );

  const moralisApiKey = process.env.REACT_APP_MORALIS_API_KEY;

  useEffect(() => {
    if (isOpen && selectedNetwork && account) {
      fetchTokens();
    }
  }, [isOpen, account, selectedNetwork, filterEnabled]);

  const fetchTokens = async () => {
    setLoading(true);
    setTokens([]);
    setManualToken(null);

    const networkInfo = NETWORK_NAME_MAP[selectedNetwork];
    const chain = networkInfo ? networkInfo.moralisChain : null;

    if (!chain) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `https://deep-index.moralis.io/api/v2.2/wallets/${account}/tokens?chain=${chain}`,
        {
          method: "GET",
          headers: {
            "x-api-key": moralisApiKey,
            accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Error fetching tokens: ${response.statusText}`);
      }

      const data = await response.json();

      const allTokens = data.result || [];
      const vettedTokens = allTokens.filter(
        (token) => token.logo && parseFloat(token.usd_price) > 0
      );

      if (filterEnabled) {
        setTokens(vettedTokens);
      } else {
        // Mark tokens that do not meet the filter criteria as unvetted
        const unvettedTokens = allTokens.map((token) => ({
          ...token,
          isUnvetted: !vettedTokens.some((vetted) => vetted.token_address === token.token_address),
        }));
        setTokens(unvettedTokens);
      }
    } catch (error) {
      console.error("Error fetching tokens:", error);
      toast.error("Failed to fetch tokens.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (searchAddress.length === 42 && searchAddress.startsWith("0x")) {
        fetchManualToken(searchAddress);
      } else {
        setManualToken(null);
      }
    }, 800);

    return () => clearTimeout(delayDebounce);
  }, [searchAddress]);

  const fetchManualToken = async (address) => {
    const networkInfo = NETWORK_NAME_MAP[selectedNetwork];
    const chain = networkInfo ? networkInfo.moralisChain : null;

    try {
      const response = await fetch(
        `https://deep-index.moralis.io/api/v2.2/wallets/${account}/tokens?chain=${chain}&token_addresses=${address}`,
        {
          method: "GET",
          headers: {
            "x-api-key": moralisApiKey,
            accept: "application/json",
          },
        }
      );

      const data = await response.json();
      const token = data.result[0];

      if (token && parseFloat(token.balance) > 0) {
        setManualToken({
          symbol: token.symbol,
          decimals: token.decimals,
          balance: token.balance,
          token_address: address,
          logo: token.logo || "https://via.placeholder.com/40",
          isUnvetted: true,
        });
      } else {
        setManualToken(null);
      }
    } catch (error) {
      console.error("Error fetching manual token:", error);
      setManualToken(null);
    }
  };

  const handleTokenSelect = (token) => {
    if (token.isUnvetted) {
      toast.warn("⚠️ This token is not vetted. Trade at your own risk!", { autoClose: 5000 });
    }
    onSelectToken(token);
    onClose();
  };

  const handleFilterToggle = () => {
    const newFilterState = !filterEnabled;
    setFilterEnabled(newFilterState);
    localStorage.setItem("filterEnabled", JSON.stringify(newFilterState));
    fetchTokens();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-md p-6 bg-gray-900 rounded-lg shadow-lg">
        <h2 className="mb-4 text-xl font-bold text-white">Select a Token</h2>

        {/* Search Input */}
        <div className="mb-4">
          <input
            type="text"
            placeholder="Enter token contract address"
            value={searchAddress}
            onChange={(e) => setSearchAddress(e.target.value)}
            className="w-full px-4 py-2 text-white bg-gray-700 rounded-lg focus:outline-none"
          />
        </div>

        {/* Filter Toggle */}
        <div className="flex items-center mb-4">
          <input
            id="filter-toggle"
            type="checkbox"
            checked={filterEnabled}
            onChange={handleFilterToggle}
            className="mr-2"
          />
          <label htmlFor="filter-toggle" className="text-sm text-gray-300">
            Enable vetted token filter
          </label>
        </div>

        {/* Networks and Tokens */}
        <div className="flex">
          {/* Networks */}
          <div className="w-1/3 pr-4 border-r border-gray-700">
            <h3 className="mb-4 text-lg font-semibold text-white">Networks</h3>
            {["BSC", "BASE"].map((network) => (
              <div
                key={network}
                className={`flex items-center mb-4 cursor-pointer hover:bg-gray-800 p-2 rounded ${
                  selectedNetwork === network ? "bg-gray-700" : ""
                }`}
                onClick={() => onSelectNetwork(network)}
              >
                <img
                  src={`https://github.com/divechan/brandkit/blob/main/${network.toLowerCase()}.png?raw=true`}
                  alt={network}
                  className="w-8 h-8 rounded-full"
                />
                <span className="ml-3 text-white">{network}</span>
              </div>
            ))}
          </div>

          {/* Tokens */}
          <div className="w-2/3 pl-4">
            <h3 className="mb-4 text-lg font-semibold text-white">Tokens</h3>
            <div className="overflow-y-auto max-h-64 scrollbar-thin scrollbar-thumb-blue-600 scrollbar-track-gray-800">
              {/* Manual Token */}
              {manualToken && (
                <div
                  className="flex items-center p-2 mb-4 bg-red-700 rounded cursor-pointer hover:bg-red-600"
                  onClick={() => handleTokenSelect(manualToken)}
                >
                  <img src={manualToken.logo} alt={manualToken.symbol} className="w-8 h-8 rounded-full" />
                  <div className="ml-3">
                    <span className="font-medium text-white">{manualToken.symbol}</span>
                    <div className="text-sm text-gray-400">⚠️ Unvetted</div>
                  </div>
                </div>
              )}

              {loading ? (
                <div className="text-gray-400">Loading tokens...</div>
              ) : tokens.length > 0 ? (
                tokens.map((token) => (
                  <div
                    key={token.token_address}
                    className={`flex items-center p-2 mb-4 rounded cursor-pointer hover:bg-gray-800 ${
                      token.isUnvetted ? "bg-red-700 hover:bg-red-600" : ""
                    }`}
                    onClick={() => handleTokenSelect(token)}
                  >
                    <img
                      src={token.logo || "https://via.placeholder.com/40"}
                      alt={token.symbol}
                      className="w-8 h-8 rounded-full"
                    />
                    <div className="ml-3">
                      <span className="font-medium text-white">{token.symbol}</span>
                      <div className="text-sm text-gray-400">
                        Balance: {(token.balance / Math.pow(10, token.decimals)).toFixed(4)}
                      </div>
                      {token.isUnvetted && (
                        <div className="text-sm text-gray-300">
                          ⚠️ Unvetted
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-gray-400">No tokens available</div>
              )}
            </div>
          </div>
        </div>

        <button
          className="w-full px-4 py-2 mt-4 text-white bg-blue-600 rounded-lg hover:bg-blue-500"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  );
};

// ProgressModal Component
const ProgressModal = ({ isOpen, onClose, steps }) => {
  // Check if all steps are completed
  const allCompleted = steps.every((step) => step.completed);

  return (
    isOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="p-6 bg-gray-800 rounded-lg shadow-lg w-96">
          <h3 className="mb-4 text-lg font-semibold text-white">
            Transaction Progress
          </h3>
          <ul>
            {steps.map((step, index) => (
              <li key={index} className="flex items-center mb-2">
                {step.failed ? (
                  <span className="inline-block w-4 h-4 mr-2 bg-red-500 rounded-full"></span>
                ) : step.completed ? (
                  <span className="inline-block w-4 h-4 mr-2 bg-green-500 rounded-full"></span>
                ) : step.pending ? (
                  <span className="inline-block w-4 h-4 mr-2 border-4 border-blue-400 rounded-full border-t-transparent animate-spin"></span>
                ) : (
                  <span className="inline-block w-4 h-4 mr-2 bg-gray-500 rounded-full"></span>
                )}
                <span
                  className={`text-sm ${
                    step.failed
                      ? "text-red-400"
                      : step.completed
                      ? "text-green-400"
                      : "text-gray-300"
                  }`}
                >
                  {step.name}
                </span>
              </li>
            ))}
          </ul>
          <button
            className={`w-full px-4 py-2 mt-4 text-white rounded-lg ${
              allCompleted ? "bg-green-600 hover:bg-green-500" : "bg-blue-600 hover:bg-blue-500"
            }`}
            onClick={onClose}
          >
            {allCompleted ? "Done" : "Close"}
          </button>
        </div>
      </div>
    )
  );
};

// SwapCard Component
const SwapCard = ({ walletAddress }) => {
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [payToken, setPayToken] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [outputAmount, setOutputAmount] = useState("0.0");
  const [outputUsdValue, setOutputUsdValue] = useState("0.0");
  const [account, setAccount] = useState("");
  const [maxBalance, setMaxBalance] = useState("0");
  const [nativeBalance, setNativeBalance] = useState("0");
  const [wsteamxPrice, setWsteamxPrice] = useState(0);
  const networkLogos = {
    ETH: "https://github.com/divechan/brandkit/blob/main/eth.png?raw=true",
    BSC: "https://github.com/divechan/brandkit/blob/main/bsc.png?raw=true",
    BASE: "https://github.com/divechan/brandkit/blob/main/base.png?raw=true",
  };
  const [isProgressModalOpen, setIsProgressModalOpen] = useState(false);
  const [progressSteps, setProgressSteps] = useState([
    { name: "Pending Approval", pending: true, completed: false },
    { name: "Pending Confirmation", pending: false, completed: false },
    { name: "Pending Swap", pending: false, completed: false },
  ]);

  useEffect(() => {
    console.log("Pay Amount or Pay Token changed:", payAmount, payToken);
    calculateOutputAmount();
  }, [payAmount, payToken]);

  // Fetch Account on Component Mount or when walletAddress changes
  useEffect(() => {
    if (walletAddress) {
      setAccount(walletAddress);
    } else {
      fetchAccount();
    }
  }, [walletAddress]);

  const fetchAccount = async () => {
    if (!window.ethereum) {
      toast.error("MetaMask is not installed.");
      return;
    }

    try {
      const accounts = await window.ethereum.request({ method: "eth_requestAccounts" });
      if (accounts.length > 0) {
        setAccount(accounts[0]);
        toast.success("Wallet connected successfully!");
      }
    } catch (error) {
      console.error("Wallet connection cancelled:", error);
      toast.error("Wallet connection cancelled. Please try again.");
    }
  };

  // Open Token Modal with Wallet Connection Check
  const handleTokenModalOpen = () => {
    if (!walletAddress) {
      toast.error("Please connect your wallet first to select a token.");
      return;
    }
    setIsModalOpen(true);
  };

  useEffect(() => {
    const updateWsteamxPrice = async () => {
      const price = await fetchWSTEAMXPrice();
      setWsteamxPrice(price);
    };
    updateWsteamxPrice();
  }, []);

  const fetchWSTEAMXPrice = async () => {
    const priceEndpoint = "https://api.geckoterminal.com/api/v2/networks/rails-network/tokens/0x0000000000000000000000000000000000627800?include=price";
    try {
      const response = await fetch(priceEndpoint);
      if (!response.ok) throw new Error("Failed to fetch price");
      const data = await response.json();
      return parseFloat(data?.data?.attributes?.price_usd) || 0;
    } catch (error) {
      console.error("Error fetching price:", error);
      return 0;
    }
  };

  // Updated handleMaxClick Function
  const handleMaxClick = async () => {
    if (payToken) {
      try {
        const networkInfo = NETWORK_NAME_MAP[selectedNetwork];
        const sourceChain = chains.find(
          (c) => c.name.toLowerCase() === networkInfo.chainName.toLowerCase()
        );

        if (!sourceChain) {
          toast.error("Selected network is invalid.");
          return;
        }

        const provider = new ethers.JsonRpcProvider(sourceChain.rpcUrl);

        // Fetch the fee data
        const feeData = await provider.getFeeData();
        const gasPrice = feeData.gasPrice || feeData.maxFeePerGas;

        if (!gasPrice) {
          throw new Error(
            "Gas price could not be fetched. Please check the network configuration."
          );
        }

        let estimatedGasLimit;
        let gasCost;
        const stableTokenAddress = sourceChain.usdc.toLowerCase();
        const nativeTokenAddress = sourceChain.native.toLowerCase();

        // Validate key inputs
        if (!maxBalance || parseFloat(maxBalance) <= 0) {
          toast.error("Invalid max balance. Ensure sufficient balance for the transaction.");
          return;
        }
        if (!walletAddress) {
          toast.error("Wallet address not found. Connect your wallet and try again.");
          return;
        }

        // Determine token type
        const isNativeToken =
          payToken.token_address.toLowerCase() === nativeTokenAddress ||
          payToken.token_address.toLowerCase() === "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee";

        if (isNativeToken) {
          console.log("Simulating for native token...");
          const tx = {
            to: walletAddress, // Simulate a native token transfer to self
            from: walletAddress,
            value: ethers.parseUnits("0.001", "ether"), // Small test amount for dry run
          };

          // Estimate gas with proper value
          try {
            estimatedGasLimit = await provider.estimateGas(tx);
          } catch (err) {
            console.error("Gas estimation failed for native token:", err);
            toast.error("Gas estimation failed for native token. Check transaction details.");
            return;
          }

          // Calculate gas cost
          gasCost = BigInt(gasPrice.toString()) * estimatedGasLimit;
          const gasCostInNative = parseFloat(ethers.formatUnits(gasCost.toString(), 18));

          // Apply safety multiplier (e.g., 2.5)
          const safetyMultiplier = 2.5;
          const adjustedGasCost = gasCostInNative * safetyMultiplier;

          // Convert maxBalance to float for calculation
          const maxBalanceFloat = parseFloat(maxBalance);

          if (maxBalanceFloat <= adjustedGasCost) {
            toast.error(
              `Insufficient native balance. You need at least ${adjustedGasCost.toFixed(6)} ${
                sourceChain.nativesymbol
              } to cover gas fees.`
            );
            setPayAmount("0.0");
            return;
          }

          const adjustedMax = maxBalanceFloat - adjustedGasCost;

          setPayAmount(adjustedMax.toFixed(6));
          toast.success(
            `Max amount adjusted to ${adjustedMax.toFixed(
              6
            )} ${payToken.symbol} to cover estimated gas fees of ${adjustedGasCost.toFixed(
              6
            )} ${sourceChain.nativesymbol}.`
          );
        } else {
          // For non-native tokens, estimate total gas required for swap and bridge
          console.log("Estimating gas for non-native token swap and bridge...");

          // Define estimated gas limits for each step (these are approximate and should be adjusted based on actual usage)
          const gasLimits = {
            approveERC20: 60000, // Gas limit for ERC20 approval
            approveStable: 60000, // Gas limit for stablecoin approval
            swap: 200000, // Gas limit for swapping tokens
            bridge: 200000, // Gas limit for bridging
          };

          // Total gas limit
          const totalGasLimit = gasLimits.approveERC20 + gasLimits.approveStable + gasLimits.swap + gasLimits.bridge;

          // Calculate gas cost
          gasCost = BigInt(gasPrice.toString()) * BigInt(totalGasLimit);
          const gasCostInNative = parseFloat(ethers.formatUnits(gasCost.toString(), 18));

          // Check if native balance is sufficient
          const nativeBalanceFloat = parseFloat(nativeBalance);

          if (nativeBalanceFloat < gasCostInNative) {
            toast.error(
              `Insufficient native balance to cover gas fees. Estimated gas cost: ${gasCostInNative.toFixed(
                6
              )} ${sourceChain.nativesymbol}. Please add more native tokens.`
            );
            return;
          }

          // If sufficient, set payAmount to maxBalance
          setPayAmount(maxBalance);
          toast.success(
            `Max amount set to ${maxBalance} ${payToken.symbol}. Ensure you have at least ${gasCostInNative.toFixed(
              6
            )} ${sourceChain.nativesymbol} to cover gas fees.`
          );
        }
      } catch (error) {
        console.error("Error estimating gas:", error);
        toast.error("Could not estimate gas fees. Check network configuration.");
      }
    }
  };

  // Handle Token Selection
  const handleTokenSelect = async (token) => {
    if (!selectedNetwork || !token) return;

    const isNativeToken =
      token.token_address.toLowerCase() === "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee" ||
      token.token_address.toLowerCase() === chains.find((c) => c.name.toLowerCase() === selectedNetwork.toLowerCase()).native.toLowerCase();

    const decimals = isNativeToken ? 18 : token.decimals;

    setPayToken({
      ...token,
      decimals,
      isNative: isNativeToken,
    });

    console.log(`Token Selected: ${token.symbol}, Decimals: ${decimals}, Native: ${isNativeToken}`);
  };

  // Fetch Balances
  const fetchBalance = async () => {
    if (!selectedNetwork || !walletAddress) return;
    const networkInfo = NETWORK_NAME_MAP[selectedNetwork];
    if (!networkInfo) return;

    const sourceChain = chains.find(
      (c) => c.name.toLowerCase() === networkInfo.chainName.toLowerCase()
    );

    if (!sourceChain) {
      console.error("Source chain not found for selected network:", selectedNetwork);
      toast.error("Selected network is not supported.");
      return;
    }

    try {
      const provider = new JsonRpcProvider(sourceChain.rpcUrl);

      // Fetch native balance
      const nativeBal = await provider.getBalance(walletAddress);
      setNativeBalance(formatUnits(nativeBal, 18)); // Assuming 18 decimals for native tokens

      // Fetch ERC20 token balance
      if (!payToken) {
        setMaxBalance("0");
        return;
      }

      if (
        payToken.token_address.toLowerCase() ===
        "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee"
      ) {
        const balance = nativeBal;
        setMaxBalance(formatUnits(balance, 18));
      } else {
        const erc20Contract = new Contract(payToken.token_address, ERC20_ABI, provider);
        const balance = await erc20Contract.balanceOf(walletAddress);
        setMaxBalance(formatUnits(balance, payToken.decimals));
      }
    } catch (error) {
      console.error("Error fetching balance:", error);
      toast.error("Failed to fetch balance.");
    }
  };

  useEffect(() => {
    fetchBalance();
  }, [payToken, selectedNetwork]);

  // Calculate Output Amount
  const calculateOutputAmount = async () => {
    if (!payToken || !payAmount || !selectedNetwork) return;

    const networkInfo = NETWORK_NAME_MAP[selectedNetwork];
    if (!networkInfo) return;

    const sourceChain = chains.find((chain) => chain.name === networkInfo.chainName);
    const targetChain = chains.find((chain) => chain.id === 6278); // Rails
    if (!sourceChain || !targetChain) return;

    try {
      const providerSource = new JsonRpcProvider(sourceChain.rpcUrl);
      const providerTarget = new JsonRpcProvider(targetChain.rpcUrl);

      const payTokenDecimals = payToken.decimals;

      const sourceStablecoin = outTokens.find(
        (t) => t.chainId === sourceChain.id && t.token_address.toLowerCase() === sourceChain.usdc.toLowerCase()
      );
      const sourceStablecoinDecimals = sourceStablecoin ? sourceStablecoin.decimals : 18;

      const targetStablecoin = outTokens.find(
        (t) => t.chainId === targetChain.id && t.token_address.toLowerCase() === targetChain.usdc.toLowerCase()
      );
      const targetStablecoinDecimals = targetStablecoin ? targetStablecoin.decimals : 6;

      const WSTEAMX_DECIMALS = WSTEAMX.decimals;

      const inputAmountBN = parseUnits(payAmount, payTokenDecimals);
      console.log(`Input Amount (BN): ${inputAmountBN.toString()}`);

      const uniswapRouterSource = new Contract(
        sourceChain.uniswapV2RouterAddress,
        UNISWAP_V2_ROUTER_ABI,
        providerSource
      );

      let usdcAmount;
      let pathSource;

      if (payToken.token_address.toLowerCase() === sourceChain.usdc.toLowerCase()) {
        usdcAmount = inputAmountBN;
        pathSource = [sourceStablecoin.token_address];
      } else if (
        payToken.token_address.toLowerCase() === "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee" ||
        payToken.token_address.toLowerCase() === sourceChain.native.toLowerCase()
      ) {
        pathSource = [sourceChain.native, sourceStablecoin.token_address];
        const amountsOutSource = await uniswapRouterSource.getAmountsOut(inputAmountBN, pathSource);
        usdcAmount = amountsOutSource[amountsOutSource.length - 1];
      } else {
        pathSource = [payToken.token_address, sourceChain.native, sourceStablecoin.token_address];
        const amountsOutSource = await uniswapRouterSource.getAmountsOut(inputAmountBN, pathSource);
        usdcAmount = amountsOutSource[amountsOutSource.length - 1];
      }

      if (typeof usdcAmount !== "bigint" || usdcAmount === 0n) {
        throw new Error("Calculated USDC amount is zero.");
      }

      const floatUsdcSource = parseFloat(formatUnits(usdcAmount, sourceStablecoinDecimals));
      const usdcOnTargetBN = parseUnits(
        floatUsdcSource.toFixed(targetStablecoinDecimals),
        targetStablecoinDecimals
      );

      const uniswapRouterTarget = new Contract(
        targetChain.uniswapV2RouterAddress,
        UNISWAP_V2_ROUTER_ABI,
        providerTarget
      );

      const pathTarget = [targetChain.usdc, WSTEAMX.address];
      const amountsOutTarget = await uniswapRouterTarget.getAmountsOut(usdcOnTargetBN, pathTarget);

      const output = amountsOutTarget[amountsOutTarget.length - 1];
      const outputFormatted = parseFloat(formatUnits(output, WSTEAMX_DECIMALS));

      if (output === 0n) {
        throw new Error("Calculated STEAMX output amount is zero.");
      }

      setOutputAmount(outputFormatted.toFixed(6));
      setOutputUsdValue((outputFormatted * wsteamxPrice).toFixed(2));
    } catch (error) {
      console.error("Error calculating output amount:", error);
      setOutputAmount("0.0");
      setOutputUsdValue("0.0");
    }
  };

  // Handle ERC20 Allowance
  const handleGetAllowance = async () => {
    if (!account || !payToken) return;

    if (payToken.token_address.toLowerCase() === "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee") {
      return Infinity;
    }

    const networkInfo = NETWORK_NAME_MAP[selectedNetwork];
    if (!networkInfo) return;

    const sourceChain = chains.find((chain) => chain.name === networkInfo.chainName);
    if (!sourceChain) return;

    const provider = new BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();
    const erc20Contract = new Contract(payToken.token_address, ERC20_ABI, signer);

    // Checking code is optional, we can trust Moralis tokens or skip it
    const spender = sourceChain.uniswapV2RouterAddress;
    try {
      const allowance = await erc20Contract.allowance(account, spender);
      return parseFloat(formatUnits(allowance, payToken.decimals));
    } catch (error) {
      console.error('Error getting token allowance:', error);
      return -1;
    }
  };

  // Handle ERC20 Approval
  const handleApprove = async () => {
    if (!account || !payToken) return;

    const networkInfo = NETWORK_NAME_MAP[selectedNetwork];
    if (!networkInfo) return;

    const sourceChain = chains.find((chain) => chain.name === networkInfo.chainName);
    if (!sourceChain) return;

    const provider = new BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();
    const erc20Contract = new Contract(payToken.token_address, ERC20_ABI, signer);

    // Approve the contract address for max possible value
    const spender = contracts[sourceChain.symbol.toLowerCase()];
    const MAX_UINT256 = BigInt("0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff");

    try {
      const tx = await erc20Contract.approve(spender, MAX_UINT256);
      await tx.wait();
      toast.success(`Approval successful! You approved max spending for ${payToken.symbol}`);
    } catch (error) {
      console.error("Approval failed:", error);
      toast.error("Approval failed. Please try again.");
    }
  };

  // Reset Progress Steps Based on Token Type
  const resetSteps = (tokenType) => {
    let steps;
    if (tokenType === "native") {
      steps = [
        { name: "Pending Approval for Stable Coin", pending: true, completed: false },
        { name: "Swapping Native for Stable Coin", pending: false, completed: false },
        { name: "Swapping for STEAMX", pending: false, completed: false },
        { name: "Completed", pending: false, completed: false },
      ];
    } else if (tokenType === "stable") {
      steps = [
        { name: "Pending Approval for Stable Coin", pending: true, completed: false },
        { name: "Swapping for STEAMX", pending: false, completed: false },
        { name: "Completed", pending: false, completed: false },
      ];
    } else if (tokenType === "erc20") {
      steps = [
        { name: "Approving ERC20 Token", pending: true, completed: false },
        { name: "Approving Stable Coin for Bridging", pending: false, completed: false },
        { name: "Swapping for STEAMX", pending: false, completed: false },
        { name: "Bridging STEAMX", pending: false, completed: false },
        { name: "Completed", pending: false, completed: false },
      ];
    }
    setProgressSteps(steps);
  };

  // Update Progress Steps
  const updateStepProgress = (stepIndex) => {
    setProgressSteps(prevSteps =>
      prevSteps.map((step, index) => {
        if (index === stepIndex) return { ...step, pending: false, completed: true };
        if (index === stepIndex + 1) return { ...step, pending: true };
        return step;
      })
    );
  };

  // Handle Swap
  const handleSwap = async () => {
    if (!payToken || payAmount === "" || !selectedNetwork || !walletAddress) {
      toast.error("Please fill all fields before proceeding.");
      return;
    }

    if (parseFloat(payAmount) <= 0) {
      toast.error("Input amount must be greater than 0.");
      return;
    }

    // Perform a case-insensitive search
    const sourceChain = chains.find(
      (chain) => chain.symbol.toLowerCase() === selectedNetwork.toLowerCase()
    );

    if (!sourceChain) {
      toast.error("Source chain not found for selected network.");
      console.error("Source chain not found:", selectedNetwork);
      return;
    }

    const isNative =
      payToken.token_address.toLowerCase() === "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee" ||
      payToken.token_address.toLowerCase() === sourceChain.native.toLowerCase();

    const isStable =
      payToken.token_address.toLowerCase() === sourceChain.usdc.toLowerCase();

    const tokenType = isNative ? "native" : isStable ? "stable" : "erc20";
    resetSteps(tokenType); // Reset steps based on token type
    setIsProgressModalOpen(true);

    try {
      const provider = new BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();

      const networkInfo = NETWORK_NAME_MAP[selectedNetwork];
      if (!networkInfo) {
        toast.error("Selected network is invalid.");
        console.error("Invalid network selected:", selectedNetwork);
        return;
      }

      const sourceChain = chains.find((chain) => chain.name === networkInfo.chainName);
      if (!sourceChain) {
        toast.error("Source chain is not supported.");
        console.error("Source chain not found:", selectedNetwork);
        return;
      }

      if (!sourceChain.usdc || !sourceChain.native) {
        toast.error("Missing necessary token addresses (usdc/native) for the selected network.");
        console.error("Missing properties in source chain:", sourceChain);
        return;
      }

      const bridgingContractAddress = contracts[sourceChain.symbol.toLowerCase()];
      if (!bridgingContractAddress) throw new Error("Bridging contract address not found.");

      const bridgingContract = new Contract(
        bridgingContractAddress,
        HYPERLANE_BRIDGE_ABI,
        signer
      );

      const uniswapRouterSource = new Contract(
        sourceChain.uniswapV2RouterAddress,
        UNISWAP_V2_ROUTER_ABI,
        signer
      );

      let inputAmountBN = parseUnits(payAmount, payToken.decimals);
      let bridgeAmountBN = inputAmountBN;

      // Handle ERC20 Tokens
      if (tokenType === "erc20") {
        const erc20Contract = new Contract(payToken.token_address, ERC20_ABI, signer);
        const MAX_UINT256 = BigInt("0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff");

        // Step 1: Approve ERC20 for swapping
        toast.info(`Approving ${payToken.symbol} for swapping...`);
        const txApproveERC20 = await erc20Contract.approve(sourceChain.uniswapV2RouterAddress, MAX_UINT256);
        await txApproveERC20.wait(); // Wait for transaction confirmation
        updateStepProgress(0);

        // Step 2: Approve Stablecoin for Bridging
        const sourceStablecoin = outTokens.find(
          (t) =>
            t.chainId === sourceChain.id &&
            t.token_address.toLowerCase() === sourceChain.usdc.toLowerCase()
        );
        if (!sourceStablecoin) throw new Error("Stablecoin not found for the selected network.");

        const stableCoinContract = new Contract(sourceStablecoin.token_address, ERC20_ABI, signer);
        toast.info(`Approving ${sourceStablecoin.symbol} for bridging...`);
        const txApproveStable = await stableCoinContract.approve(bridgingContractAddress, MAX_UINT256);
        await txApproveStable.wait(); // Wait for transaction confirmation
        updateStepProgress(1);

        // Step 3: Swap ERC20 to STEAMX
        toast.info("Swapping ERC20 to STEAMX...");
        const pathERC20ToStable = [payToken.token_address, sourceChain.native, sourceStablecoin.token_address];
        const amountsOut = await uniswapRouterSource.getAmountsOut(inputAmountBN, pathERC20ToStable);
        bridgeAmountBN = amountsOut[amountsOut.length - 1];

        const txSwap = await uniswapRouterSource.swapExactTokensForTokens(
          inputAmountBN,
          bridgeAmountBN,
          pathERC20ToStable,
          walletAddress,
          Math.floor(Date.now() / 1000) + 60 * 10 // 10-minute deadline
        );
        await txSwap.wait(); // Wait for swap confirmation
        updateStepProgress(2);

        // Step 4: Bridge STEAMX
        toast.info("Bridging STEAMX...");
        const txBridge = await bridgingContract.callRemote(
          6278,
          walletAddress,
          sourceStablecoin.token_address,
          bridgeAmountBN,
          false,
          { value: BigInt(0) }
        );
        await txBridge.wait(); // Wait for bridging confirmation
        updateStepProgress(3);
      }

      // Handle Native Tokens
      if (tokenType === "native") {
        const sourceStablecoin = outTokens.find(
          (t) =>
            t.chainId === sourceChain.id &&
            t.token_address.toLowerCase() === sourceChain.usdc.toLowerCase()
        );
        if (!sourceStablecoin) throw new Error("Stablecoin not found for the selected network.");

        // Step 1: Approve Stablecoin for Bridging
        const stableCoinContract = new Contract(sourceStablecoin.token_address, ERC20_ABI, signer);
        const MAX_UINT256 = BigInt("0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff");

        toast.info(`Approving ${sourceStablecoin.symbol} for bridging...`);
        const txApproveStable = await stableCoinContract.approve(
          bridgingContractAddress,
          MAX_UINT256
        );
        await txApproveStable.wait(); // Wait for transaction confirmation
        updateStepProgress(0);

        // Step 2: Swap Native to STEAMX
        toast.info("Swapping native token to STEAMX...");
        const pathNativeToStable = [sourceChain.native, sourceStablecoin.token_address];
        const amountsOutNativeToStable = await uniswapRouterSource.getAmountsOut(
          inputAmountBN,
          pathNativeToStable
        );

        bridgeAmountBN = amountsOutNativeToStable[amountsOutNativeToStable.length - 1];
        const txSwap = await uniswapRouterSource.swapExactETHForTokens(
          bridgeAmountBN,
          pathNativeToStable,
          walletAddress,
          Math.floor(Date.now() / 1000) + 60 * 10, // 10-minute deadline
          { value: inputAmountBN }
        );
        await txSwap.wait(); // Wait for swap confirmation
        updateStepProgress(1);

        // Step 3: Bridge STEAMX
        toast.info("Bridging STEAMX...");
        const txBridge = await bridgingContract.callRemote(
          6278,
          walletAddress,
          sourceStablecoin.token_address,
          bridgeAmountBN,
          false,
          { value: BigInt(0) }
        );
        await txBridge.wait(); // Wait for bridging confirmation
        updateStepProgress(2);
      }

      // Handle Stable Tokens
      if (tokenType === "stable") {
        const stableCoinContract = new Contract(payToken.token_address, ERC20_ABI, signer);
        const MAX_UINT256 = BigInt("0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff");

        // Step 1: Approve Stablecoin for Bridging
        toast.info(`Approving ${payToken.symbol} for bridging...`);
        const txApproveStable = await stableCoinContract.approve(bridgingContractAddress, MAX_UINT256);
        await txApproveStable.wait(); // Wait for transaction confirmation
        updateStepProgress(0);

        // Step 2: Initiate Bridging
        toast.info("Initiating bridging transaction...");
        const txBridge = await bridgingContract.callRemote(
          6278,
          walletAddress,
          payToken.token_address,
          inputAmountBN,
          false,
          { value: BigInt(0) }
        );
        await txBridge.wait(); // Wait for bridging confirmation
        updateStepProgress(1);
      }

      toast.success("Swap and bridge successful!");
      // Mark all steps as completed
      setProgressSteps((prevSteps) =>
        prevSteps.map((step) => ({
          ...step,
          pending: false,
          completed: true,
          failed: false,
        }))
      );
    } catch (error) {
      console.error("Error during swap:", error);
      toast.error("An error occurred during the swap process.");
      // Handle failure state
      setProgressSteps((prevSteps) =>
        prevSteps.map((step, index) => ({
          ...step,
          pending: false,
          completed: index === prevSteps.length - 1 ? false : step.completed,
          failed: index === prevSteps.length - 1 ? true : false,
        }))
      );
    } finally {
      // Ensure modal remains open for user interaction
      setTimeout(() => setIsProgressModalOpen(false), 6000); // Optional delay before closing
    }
  };

  // Helper function to mark a step as failed
  const setStepError = (stepIndex, stepName) => {
    setProgressSteps((prevSteps) =>
      prevSteps.map((step, index) => ({
        ...step,
        pending: false,
        completed: false,
        failed: index === stepIndex,
      }))
    );
    console.log(`${stepName} cancelled`);
  };

  // Handle Network Selection
  const handleNetworkSelect = async (network) => {
    console.log("Switching to network:", network);
    setSelectedNetwork(network);
    setPayToken(null);

    // Attempt to switch the wallet to the selected network
    const networkInfo = NETWORK_NAME_MAP[network];
    if (!networkInfo) return;

    const sourceChain = chains.find((chain) => chain.name === networkInfo.chainName);
    if (!sourceChain) return;

    // Convert chain ID to hex
    const chainIdHex = '0x' + sourceChain.id.toString(16);

    if (window.ethereum) {
      try {
        await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: chainIdHex }],
        });
        console.log(`Switched to ${sourceChain.name} network`);
      } catch (error) {
        console.error(`Failed to switch to ${sourceChain.name} network:`, error);
        // If the chain is not added in the user's MetaMask, you could add a request to add it.
        // await window.ethereum.request({
        //   method: 'wallet_addEthereumChain',
        //   params: [ { chainId: chainIdHex, chainName: sourceChain.name, rpcUrls: [sourceChain.rpcUrl], ... } ]
        // });
      }
    }
  };

  // Determine Glow Class Based on Selected Network
  const getGlowClass = () => {
    if (selectedNetwork === "ETH") return "eth";
    if (selectedNetwork === "BSC") return "bsc";
    if (selectedNetwork === "BASE") return "base";
    return "";
  };

  return (
    <div className={`swap-card ${getGlowClass()}`}>
      <h2 className="mb-5 text-xl font-bold text-left text-white">Rails: Crossrails</h2>

      {/* Pay Section */}
      <div className="mb-6">
        <label className="block mb-2 text-sm text-gray-400">Pay</label>
        <div>
          <div className="flex items-center mb-2">
            <TokenSelectorButton
              logo={payToken ? payToken.logo : null}
              tokenName={payToken ? payToken.symbol : "Select a Token"}
              onClick={handleTokenModalOpen}
            />
          </div>
          <div className="flex items-center justify-between p-2 bg-gray-800 rounded-lg">
            <input
              type="number"
              placeholder="0.0"
              value={payAmount}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "" || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0)) {
                  setPayAmount(value);
                }
              }}
              className="w-full text-lg text-white bg-transparent outline-none appearance-none"
              min="0" // Prevent negative inputs
            />
            <button
              className="ml-2 text-sm text-gray-300 transition-colors duration-100 hover:text-blue-600"
              onClick={handleMaxClick}
              disabled={!payToken}
            >
              Max
            </button>
          </div>
          <div className="flex flex-col">
            {/* Display Token Balance */}
            <span className="text-sm text-gray-400">
              Token Balance: {payToken ? `${parseFloat(maxBalance).toFixed(6)} ${payToken.symbol}` : "0.0"}
            </span>

            {/* Display Native Coin Balance */}
            <span className="text-sm text-gray-400">
              Native Balance: {parseFloat(nativeBalance).toFixed(6)}{" "}
              {chains.find(chain => chain.name.toLowerCase() === selectedNetwork?.toLowerCase())?.nativesymbol || "Native"}
            </span>
          </div>
        </div>
      </div>

      {/* Arrow Down Icon */}
      <div className="mb-6 text-center">
        <button className="transition-transform transform shadow-xg hover:scale-125">
          <FontAwesomeIcon icon={faArrowDown} className="text-2xl text-white" />
        </button>
      </div>

      {/* Receive Section */}
      <div className="mb-6">
        <label className="block mb-2 text-sm text-gray-400">Receive</label>
        <div>
          <div className="flex items-center mb-4">
            <div className="relative flex items-center p-2 shadow-md bg-gradient-to-r from-purple-800 via-purple-800 to-purple-900 rounded-xl">
              <div className="flex items-center justify-center bg-gray-900 rounded-full shadow-inner w-14 h-14">
                <img
                  src="https://github.com/divechan/brandkit/blob/main/SVGs/new-logos/mainnet.png?raw=true"
                  alt="STEAMX"
                  className="w-12 h-12 rounded-full"
                />
              </div>
              <div className="flex items-center ml-3 text-white">
                <span className="text-lg font-medium">RAILS NETWORK</span>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-lg text-white">
              {parseFloat(outputAmount).toFixed(2)} STEAMX
            </span>
            <span className="ml-2 text-lg text-gray-400">≈ ${outputUsdValue}</span>
          </div>
        </div>
      </div>

      {/* Swap Button */}
      <button
        className="w-full px-4 py-2 text-white transition-transform transform bg-blue-600 rounded-lg shadow-md hover:bg-blue-500 hover:scale-105"
        onClick={handleSwap}
        disabled={
          !payToken ||
          payAmount === "" || // Only disable if no input is provided
          !selectedNetwork
        }
      >
        Swap
      </button>

      {/* Progress Modal */}
      <ProgressModal
        isOpen={isProgressModalOpen}
        onClose={() => setIsProgressModalOpen(false)}
        steps={progressSteps}
      />

      {/* Token Modal */}
      <TokenModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSelectNetwork={handleNetworkSelect}
        onSelectToken={handleTokenSelect}
        selectedNetwork={selectedNetwork}
        account={walletAddress}
      />
    </div>
  );
};

export default SwapCard;
